import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Mail, Lock, UserPlus, LogIn } from "lucide-react";
import { toast } from "sonner";
import EmaLogo from "../../assets/ema-logo.png";
import translations from "./adminTranslations";
import { robustFetch } from "../../lib/robustFetch";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [language, setLanguage] = useState("fr");

  const t = (key) => translations[language]?.[key] || translations.fr[key] || key;

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      if (response.ok) {
        const user = await response.json();
        if (user.role === 'super_admin' || user.role === 'admin_employee') {
          toast.success(`${t("welcome")}, Admin`);
          navigate("/admin");
        } else {
          toast.error(t("access_denied"));
        }
      } else {
        toast.error(t("incorrect_credentials"));
      }
    } catch (error) {
      toast.error(t("connection_error"));
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error(t("passwords_mismatch"));
      return;
    }
    if (password.length < 6) {
      toast.error(t("password_min_length"));
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/admin/employee-signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || t("password_configured"));
        setMode("login");
        setConfirmPassword("");
      } else {
        toast.error(data.detail || t("signup_error"));
      }
    } catch (error) {
      toast.error(t("server_error"));
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    try {
      const res = await robustFetch(`${API}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ credential: credentialResponse.credential }),
        retries: 4,
      });
      const data = await res.json();
      if (res.ok && data.role === 'super_admin') {
        localStorage.setItem('ema_admin', JSON.stringify(data));
        navigate('/admin');
      } else if (res.ok) {
        toast.error('Accès réservé au Super Admin');
      } else {
        toast.error(data.detail || 'Connexion Google échouée. Merci de réessayer.');
      }
    } catch (err) {
      const msg = err.name === 'AbortError'
        ? "Réseau lent. Merci de réessayer dans un instant."
        : "Erreur de connexion Google. Merci de réessayer.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Language Selector */}
        <div className="flex justify-center gap-2 mb-6">
          {['fr', 'en', 'es'].map(lang => (
            <button
              key={lang}
              onClick={() => setLanguage(lang)}
              className={`px-3 py-1.5 text-xs rounded-md uppercase font-medium transition-colors ${
                language === lang
                  ? "bg-indigo-600 text-white"
                  : "bg-zinc-900 text-zinc-400 hover:text-zinc-200 border border-zinc-800"
              }`}
            >
              {lang}
            </button>
          ))}
        </div>

        <div className="text-center mb-8">
          <div className="mx-auto mb-4 flex items-center justify-center">
            <img src={EmaLogo} alt="Ema - Admin" className="h-20 w-auto object-contain" />
          </div>
          <h1 className="text-3xl font-display font-bold text-white mb-2">{t("admin_console")}</h1>
          <p className="text-zinc-400">{t("secure_access")}</p>
        </div>

        {/* Tabs: Sign In / Sign Up */}
        <div className="flex mb-4 bg-zinc-900 rounded-lg p-1 border border-zinc-800">
          <button
            data-testid="admin-signin-tab"
            onClick={() => setMode("login")}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              mode === "login" ? "bg-indigo-600 text-white" : "text-zinc-400 hover:text-white"
            }`}
          >
            <LogIn className="w-4 h-4" /> {t("login")}
          </button>
          <button
            data-testid="admin-signup-tab"
            onClick={() => setMode("signup")}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              mode === "signup" ? "bg-indigo-600 text-white" : "text-zinc-400 hover:text-white"
            }`}
          >
            <UserPlus className="w-4 h-4" /> {t("signup")}
          </button>
        </div>

        <Card className="bg-zinc-900 border-zinc-800 shadow-xl">
          <CardHeader>
            <CardTitle className="text-white">
              {mode === "login" ? t("login") : t("employee_signup")}
            </CardTitle>
            <CardDescription className="text-zinc-500">
              {mode === "login"
                ? t("enter_admin_credentials")
                : t("signup_employee_desc")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={mode === "login" ? handleLogin : handleSignup} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">{t("professional_email")}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    data-testid="admin-email-input"
                    className="bg-zinc-950 border-zinc-800 pl-10 text-white"
                    placeholder="employee@ema.ai"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-300">{t("password")}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    data-testid="admin-password-input"
                    type="password"
                    className="bg-zinc-950 border-zinc-800 pl-10 text-white"
                    placeholder="******"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
              </div>

              {mode === "signup" && (
                <div className="space-y-2">
                  <Label className="text-zinc-300">{t("confirm_password")}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      data-testid="admin-confirm-password-input"
                      type="password"
                      className="bg-zinc-950 border-zinc-800 pl-10 text-white"
                      placeholder="******"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                  </div>
                </div>
              )}

              <Button
                data-testid="admin-submit-btn"
                type="submit"
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                disabled={loading}
              >
                {loading ? t("loading") : mode === "login" ? t("sign_in") : t("set_password")}
              </Button>
            </form>

            {mode === "login" && (
              <div className="mt-6 pt-6 border-t border-zinc-800">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => {}}
                  text="continue_with"
                  shape="rectangular"
                  width="100%"
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
